// RouteProfile.tsx — Terrain elevation profile along a route.
//
// Hand-rolled SVG chart (matches the existing WBEnvelopeChart and
// track-log profile precedents — no chart library). Shows:
//   - Terrain ridge (filled brown polygon)
//   - Planned cruise altitude as a horizontal ember line
//   - AGL-clearance band shaded green / amber / red
//   - Waypoint markers as vertical ticks with idents along the X axis
//   - Hover ruler + numeric callout: distance, lat/lon, MSL, AGL
//
// Boss request 2026-05-19 02:01 PT. See data/route-profile-plan.md.

import { useEffect, useMemo, useRef, useState } from 'react';
import { sampleRouteProfile, computeAgl, type ProfileSample, type ProfileWaypoint } from '../lib/routeTerrainProfile';

// ── Tunables ─────────────────────────────────────────────────────────────

const CHART_PAD = { top: 12, right: 14, bottom: 24, left: 48 };
const DEFAULT_HEIGHT = 200;
const DEFAULT_AGL_MIN_FT = 1000; // green threshold
const AGL_AMBER_FT = 500;        // amber-to-red threshold

// ── Helpers ──────────────────────────────────────────────────────────────

function fmtAlt(v: number): string {
  if (Math.abs(v) >= 10000) return `${(v / 1000).toFixed(0)}k`;
  if (Math.abs(v) >= 1000) return Math.round(v).toLocaleString();
  return v.toFixed(0);
}

function fmtDist(nm: number): string {
  if (nm >= 100) return Math.round(nm).toString();
  return nm.toFixed(1);
}

/** Generate ~`count` round-number ticks between min and max. */
function niceTicks(min: number, max: number, count: number): number[] {
  if (max <= min) return [min];
  const range = max - min;
  const rough = range / count;
  const exp = Math.pow(10, Math.floor(Math.log10(rough)));
  const fracs = [1, 2, 2.5, 5, 10];
  let step = exp;
  for (const f of fracs) {
    if (f * exp >= rough) { step = f * exp; break; }
  }
  const first = Math.ceil(min / step) * step;
  const ticks: number[] = [];
  for (let v = first; v <= max + 1e-6; v += step) {
    ticks.push(Number(v.toFixed(6)));
    if (ticks.length > 20) break;
  }
  return ticks;
}

// ── Component ────────────────────────────────────────────────────────────

export interface RouteProfileProps {
  /** Ordered route waypoints. The chart uses the great-circle path
   *  between consecutive points to sample elevations. Need >=2. */
  waypoints: ProfileWaypoint[];
  /** Planned cruise altitude in feet MSL. Used to draw the cruise line
   *  and compute AGL clearance bands. Pass null to skip the AGL
   *  overlay. */
  cruiseAltFt: number | null;
  /** Optional sampling step in NM. Default 0.5. */
  stepNm?: number;
  /** Optional fixed pixel height. Default 200. */
  heightPx?: number;
  /** Optional AGL threshold below which terrain shows amber. Default 1000 ft. */
  agleMinFt?: number;
  /** Optional callback fired on hover with the nearest sample. */
  onHover?: (s: (ProfileSample & { aglFt: number | null }) | null) => void;
  /** When true, render compact (no min-AGL summary, smaller axes). */
  compact?: boolean;
}

/**
 * Stable content key for the waypoint set so we only re-fetch when the
 * route actually changes. (Lat/lon rounded to 5 decimals = ~1 m.)
 */
function waypointsKey(wp: ProfileWaypoint[]): string {
  return wp.map((w) => `${w.lat.toFixed(5)},${w.lon.toFixed(5)}`).join('|');
}

export function RouteProfile({
  waypoints,
  cruiseAltFt,
  stepNm = 0.5,
  heightPx = DEFAULT_HEIGHT,
  agleMinFt = DEFAULT_AGL_MIN_FT,
  onHover,
  compact = false,
}: RouteProfileProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number>(600);
  const [samples, setSamples] = useState<ProfileSample[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  // Track container width for responsive SVG.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setWidth(Math.round(w));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const routeKey = useMemo(() => waypointsKey(waypoints), [waypoints]);

  // Fetch profile when route changes.
  useEffect(() => {
    if (waypoints.length < 2) {
      setSamples(null);
      setError(null);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    sampleRouteProfile(waypoints, { stepNm, signal: ctrl.signal })
      .then((r) => {
        if (ctrl.signal.aborted) return;
        if (!r) {
          setSamples(null);
          setError('Profile unavailable');
        } else {
          setSamples(r.samples);
        }
      })
      .catch(() => {
        if (!ctrl.signal.aborted) {
          setSamples(null);
          setError('Profile fetch failed');
        }
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey, stepNm]);

  const enriched = useMemo(() => {
    if (!samples) return null;
    return computeAgl(samples, cruiseAltFt);
  }, [samples, cruiseAltFt]);

  // Compute bounds.
  const bounds = useMemo(() => {
    if (!enriched || enriched.length < 2) return null;
    let minE = Infinity;
    let maxE = -Infinity;
    let maxD = 0;
    for (const s of enriched) {
      if (s.elevFt != null) {
        if (s.elevFt < minE) minE = s.elevFt;
        if (s.elevFt > maxE) maxE = s.elevFt;
      }
      if (s.distNm > maxD) maxD = s.distNm;
    }
    if (!Number.isFinite(minE) || !Number.isFinite(maxE)) return null;
    // Pull Y range out so the line isn't flush against the axis.
    // Always include cruise line in the visible band.
    if (cruiseAltFt != null && Number.isFinite(cruiseAltFt)) {
      if (cruiseAltFt > maxE) maxE = cruiseAltFt;
      if (cruiseAltFt < minE) minE = cruiseAltFt;
    }
    const span = Math.max(500, maxE - minE);
    minE = Math.max(0, minE - span * 0.1);
    maxE = maxE + span * 0.1;
    return { minE, maxE, maxD: Math.max(maxD, 1) };
  }, [enriched, cruiseAltFt]);

  // Min-AGL summary for the header.
  const minAgl = useMemo(() => {
    if (!enriched || cruiseAltFt == null) return null;
    let m: number | null = null;
    let mIdx = -1;
    for (let i = 0; i < enriched.length; i++) {
      const s = enriched[i];
      if (s.aglFt == null) continue;
      if (m == null || s.aglFt < m) { m = s.aglFt; mIdx = i; }
    }
    return m == null ? null : { aglFt: m, sample: enriched[mIdx] };
  }, [enriched, cruiseAltFt]);

  /**
   * Terrain-strike points: where cruiseAlt - terrain crosses the
   * `agleMinFt` threshold. We surface these as ⚠ icons on the
   * profile so pilots can see at-a-glance WHERE along the route a
   * given cruise altitude is in conflict with terrain. Used by the
   * terrain-clearance auto-router result preview.
   *
   * Boss request 2026-05-19 07:38 PT.
   */
  const strikePoints = useMemo(() => {
    if (!enriched || cruiseAltFt == null) return [];
    const out: Array<{ distNm: number; terrainFt: number; severity: 'amber' | 'red' }> = [];
    let inSegment = false;
    let segMaxIdx = -1;
    let segMaxAgl = Infinity;
    const ENTER = agleMinFt;          // entering threshold (mild warning)
    const finishSeg = () => {
      if (segMaxIdx >= 0) {
        const s = enriched[segMaxIdx];
        const t = s.elevFt ?? cruiseAltFt;
        out.push({
          distNm: s.distNm,
          terrainFt: t,
          severity: segMaxAgl < AGL_AMBER_FT ? 'red' : 'amber',
        });
      }
      segMaxIdx = -1;
      segMaxAgl = Infinity;
      inSegment = false;
    };
    for (let i = 0; i < enriched.length; i++) {
      const s = enriched[i];
      const agl = s.aglFt;
      if (agl == null) continue;
      if (agl < ENTER) {
        inSegment = true;
        if (agl < segMaxAgl) { segMaxAgl = agl; segMaxIdx = i; }
      } else if (inSegment) {
        finishSeg();
      }
    }
    if (inSegment) finishSeg();
    return out;
  }, [enriched, cruiseAltFt, agleMinFt]);

  const innerW = Math.max(50, width - CHART_PAD.left - CHART_PAD.right);
  const innerH = Math.max(60, heightPx - CHART_PAD.top - CHART_PAD.bottom);

  // ── Empty / loading states ─────────────────────────────────────────────
  if (waypoints.length < 2) {
    return (
      <div className="px-3 py-2 text-[11px] text-cockpit-400 italic">
        Add a destination waypoint to see the terrain profile.
      </div>
    );
  }
  if (loading && !samples) {
    return (
      <div ref={containerRef} style={{ height: heightPx }} className="grid place-items-center text-cockpit-400 text-xs">
        Loading terrain profile…
      </div>
    );
  }
  if (error && !samples) {
    return (
      <div className="px-3 py-2 text-[11px] text-amber-300">
        {error}. Tap the route to retry.
      </div>
    );
  }
  if (!enriched || !bounds) {
    return (
      <div ref={containerRef} style={{ height: heightPx }} />
    );
  }

  // ── Coordinate helpers ─────────────────────────────────────────────────
  const xOf = (nm: number) => CHART_PAD.left + (nm / bounds.maxD) * innerW;
  const yOf = (ft: number) => CHART_PAD.top + (1 - (ft - bounds.minE) / (bounds.maxE - bounds.minE)) * innerH;

  // Build the terrain polygon (fill from baseline up to terrain ridge).
  const ridgeParts: string[] = [];
  const baseY = yOf(bounds.minE);
  for (let i = 0; i < enriched.length; i++) {
    const s = enriched[i];
    if (s.elevFt == null) continue;
    const x = xOf(s.distNm);
    const y = yOf(s.elevFt);
    ridgeParts.push(`${i === 0 || ridgeParts.length === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`);
  }
  // Close to baseline for the fill.
  let ridgePath = ridgeParts.join('');
  if (ridgeParts.length >= 2) {
    const lastX = xOf(enriched[enriched.length - 1].distNm);
    const firstX = xOf(enriched[0].distNm);
    ridgePath = ridgeParts.join('') + `L${lastX.toFixed(1)},${baseY.toFixed(1)}L${firstX.toFixed(1)},${baseY.toFixed(1)}Z`;
  }

  // Cruise line.
  const cruiseY = cruiseAltFt != null && Number.isFinite(cruiseAltFt) ? yOf(cruiseAltFt) : null;

  // Y ticks.
  const yTicks = niceTicks(bounds.minE, bounds.maxE, compact ? 3 : 4);
  // X ticks (NM).
  const xTickCount = Math.max(2, Math.min(8, Math.floor(bounds.maxD / 10)));
  const xTicks = niceTicks(0, bounds.maxD, xTickCount);

  // Waypoint markers.
  const wpMarkers: Array<{ x: number; label: string; showLabel: boolean }> = [];
  // The first sample is wp[0], the last is wp[last]. We approximate
  // by accumulating leg distances.
  if (waypoints.length === enriched.length || waypoints.length >= 2) {
    // Use the first/last from enriched, intermediate waypoints from cumulative.
    let cum = 0;
    wpMarkers.push({ x: xOf(0), label: waypoints[0].ident ?? 'A', showLabel: true });
    for (let i = 1; i < waypoints.length; i++) {
      const dLat = waypoints[i].lat - waypoints[i - 1].lat;
      const dLon = waypoints[i].lon - waypoints[i - 1].lon;
      const meanLat = (waypoints[i].lat + waypoints[i - 1].lat) / 2;
      const dxnm = dLon * 60 * Math.cos((meanLat * Math.PI) / 180);
      const dynm = dLat * 60;
      cum += Math.sqrt(dxnm * dxnm + dynm * dynm);
      const x = xOf(Math.min(bounds.maxD, cum));
      const label = waypoints[i].ident ?? (i === waypoints.length - 1 ? 'B' : `${i + 1}`);
      wpMarkers.push({ x, label, showLabel: true });
    }
  }

  // Label thinning — boss 2026-05-21 20:56 PDT: dense routes (lots of
  // waypoints) crowd the labels under each other. Keep every waypoint's
  // dashed tick line, but only show labels where they have horizontal room
  // to breathe. Always keep first + last (origin + destination); for the
  // rest, greedy walk from left and drop any label whose x-position is
  // within minLabelSpacing pixels of the most-recently-kept label.
  // Rough monospace width at fontSize=9: ~6.4 px/char. Five-letter idents
  // need ~32 px. Pad to 44 px for visual comfort.
  if (wpMarkers.length > 2) {
    const minLabelSpacing = 44;
    let lastShownX = wpMarkers[0].x;
    const lastIdx = wpMarkers.length - 1;
    for (let i = 1; i < lastIdx; i++) {
      if (wpMarkers[i].x - lastShownX < minLabelSpacing) {
        wpMarkers[i].showLabel = false;
      } else {
        lastShownX = wpMarkers[i].x;
      }
    }
    // Always keep destination; if it'd collide with the previously-kept
    // label, hide the intermediate one(s) walking back from the end.
    if (wpMarkers[lastIdx].x - lastShownX < minLabelSpacing) {
      for (let j = lastIdx - 1; j >= 1; j--) {
        if (wpMarkers[j].showLabel) {
          wpMarkers[j].showLabel = false;
          break;
        }
      }
    }
  }

  // Hover handling. Captures the current bounds + enriched arrays in
  // locals so TS knows they're non-null inside the closure.
  const hoverBounds = bounds;
  const hoverEnriched = enriched;
  function pointerMove(e: React.PointerEvent<SVGSVGElement>) {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const xPx = ((e.clientX - rect.left) / rect.width) * width;
    if (xPx < CHART_PAD.left || xPx > CHART_PAD.left + innerW) {
      setHoverIdx(null);
      onHover?.(null);
      return;
    }
    const targetNm = ((xPx - CHART_PAD.left) / innerW) * hoverBounds.maxD;
    let lo = 0, hi = hoverEnriched.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (hoverEnriched[mid].distNm < targetNm) lo = mid + 1;
      else hi = mid;
    }
    const cands = [hoverEnriched[Math.max(0, lo - 1)], hoverEnriched[lo]].filter(Boolean);
    let best = cands[0];
    for (const c of cands) {
      if (Math.abs(c.distNm - targetNm) < Math.abs(best.distNm - targetNm)) best = c;
    }
    const idx = hoverEnriched.indexOf(best);
    setHoverIdx(idx);
    onHover?.(best);
  }
  function pointerLeave() {
    setHoverIdx(null);
    onHover?.(null);
  }

  const hoverSample = hoverIdx != null ? enriched[hoverIdx] : null;
  const hoverX = hoverSample ? xOf(hoverSample.distNm) : null;

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div ref={containerRef} className="relative">
      {/* Header summary */}
      {!compact && minAgl != null && (
        <div className="mb-1 flex items-center justify-between text-[11px] text-cockpit-300 px-1">
          <div>
            Max terrain:{' '}
            <span className="font-mono text-amber-200">
              {fmtAlt((() => {
                let m = -Infinity;
                for (const s of enriched) if (s.elevFt != null && s.elevFt > m) m = s.elevFt;
                return m;
              })())} ft
            </span>
          </div>
          <div>
            Min AGL:{' '}
            <span className={`font-mono ${
              minAgl.aglFt >= agleMinFt
                ? 'text-emerald-300'
                : minAgl.aglFt >= AGL_AMBER_FT
                  ? 'text-amber-300'
                  : 'text-red-400'
            }`}>
              {minAgl.aglFt < 0 ? `${fmtAlt(-minAgl.aglFt)} BELOW` : `${fmtAlt(minAgl.aglFt)} ft`}
            </span>
          </div>
        </div>
      )}

      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${heightPx}`}
        width={width}
        height={heightPx}
        className="block touch-none w-full"
        preserveAspectRatio="none"
        onPointerMove={pointerMove}
        onPointerLeave={pointerLeave}
      >
        {/* AGL clearance background bands (drawn behind everything else). */}
        {cruiseAltFt != null && cruiseY != null && (
          <>
            {/* Red zone: below cruise - 500 ft is dangerous */}
            <rect
              x={CHART_PAD.left}
              y={yOf(cruiseAltFt - AGL_AMBER_FT)}
              width={innerW}
              height={Math.max(0, yOf(bounds.minE) - yOf(cruiseAltFt - AGL_AMBER_FT))}
              fill="rgba(239, 68, 68, 0.10)"
            />
            {/* Amber zone: 500-1000 ft below cruise */}
            <rect
              x={CHART_PAD.left}
              y={yOf(cruiseAltFt - agleMinFt)}
              width={innerW}
              height={Math.max(0, yOf(cruiseAltFt - AGL_AMBER_FT) - yOf(cruiseAltFt - agleMinFt))}
              fill="rgba(245, 158, 11, 0.10)"
            />
            {/* Green zone: above cruise - 1000 ft */}
            <rect
              x={CHART_PAD.left}
              y={yOf(cruiseAltFt - agleMinFt)}
              width={innerW}
              height={Math.max(0, yOf(cruiseAltFt - agleMinFt) - CHART_PAD.top)}
              fill="rgba(16, 185, 129, 0.06)"
            />
          </>
        )}

        {/* Y grid + labels */}
        {yTicks.map((v) => (
          <g key={`y${v}`}>
            <line
              x1={CHART_PAD.left}
              x2={width - CHART_PAD.right}
              y1={yOf(v)}
              y2={yOf(v)}
              stroke="#27303d"
              strokeWidth={1}
            />
            <text
              x={CHART_PAD.left - 4}
              y={yOf(v) + 3}
              textAnchor="end"
              fontSize={10}
              fill="#7a8896"
              fontFamily="ui-monospace,monospace"
            >
              {fmtAlt(v)}
            </text>
          </g>
        ))}

        {/* X grid + labels */}
        {xTicks.map((nm) => (
          <g key={`x${nm}`}>
            <line
              x1={xOf(nm)}
              x2={xOf(nm)}
              y1={CHART_PAD.top}
              y2={heightPx - CHART_PAD.bottom}
              stroke="#27303d"
              strokeWidth={1}
            />
            <text
              x={xOf(nm)}
              y={heightPx - CHART_PAD.bottom + 12}
              textAnchor="middle"
              fontSize={10}
              fill="#7a8896"
              fontFamily="ui-monospace,monospace"
            >
              {fmtDist(nm)}
            </text>
          </g>
        ))}

        {/* Terrain ridge (filled brown). */}
        {ridgePath && (
          <path
            d={ridgePath}
            fill="rgba(120, 80, 40, 0.85)"
            stroke="#8b5e2b"
            strokeWidth={0.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}

        {/* Cruise altitude line */}
        {cruiseY != null && (
          <>
            <line
              x1={CHART_PAD.left}
              x2={width - CHART_PAD.right}
              y1={cruiseY}
              y2={cruiseY}
              stroke="#f59e0b"
              strokeWidth={1.5}
              strokeDasharray="6 4"
            />
            <text
              x={width - CHART_PAD.right - 4}
              y={cruiseY - 4}
              textAnchor="end"
              fontSize={10}
              fill="#f59e0b"
              fontFamily="ui-monospace,monospace"
            >
              Cruise {fmtAlt(cruiseAltFt!)}
            </text>
          </>
        )}

        {/* Waypoint markers — tick line for every waypoint, label only when
            there's horizontal room (see thinning step above). The dropped
            labels still get their dashed tick so the geometry is intact;
            you just don't see the ident text. */}
        {wpMarkers.map((wp, i) => (
          <g key={`wp-${i}`}>
            <line
              x1={wp.x}
              x2={wp.x}
              y1={CHART_PAD.top}
              y2={heightPx - CHART_PAD.bottom}
              stroke="#a3b8d4"
              strokeOpacity={wp.showLabel ? 0.45 : 0.25}
              strokeWidth={0.8}
              strokeDasharray="2 2"
            />
            {wp.showLabel && (
              <text
                x={wp.x}
                y={CHART_PAD.top - 2}
                textAnchor="middle"
                fontSize={9}
                fill="#a3b8d4"
                fontFamily="ui-monospace,monospace"
              >
                {wp.label}
              </text>
            )}
          </g>
        ))}

        {/* Terrain-strike markers (⚠) where cruise altitude clears
            terrain by less than agleMinFt. Red triangle for genuine
            conflicts (<500 ft AGL), amber for marginal (500–999 ft).
            Boss request 2026-05-19 07:38 PT. */}
        {strikePoints.map((sp, i) => {
          const sx = xOf(sp.distNm);
          const sy = yOf(sp.terrainFt);
          const color = sp.severity === 'red' ? '#ef4444' : '#f59e0b';
          return (
            <g key={`strike-${i}`}>
              {/* Triangle (warning glyph) sitting on the terrain crest */}
              <polygon
                points={`${sx - 6},${sy - 2} ${sx + 6},${sy - 2} ${sx},${sy - 13}`}
                fill={color}
                stroke="#0b0d11"
                strokeWidth={0.8}
              />
              <text
                x={sx}
                y={sy - 4}
                textAnchor="middle"
                fontSize={9}
                fontWeight="bold"
                fill="#0b0d11"
                fontFamily="ui-sans-serif,system-ui,sans-serif"
              >
                !
              </text>
            </g>
          );
        })}

        {/* Hover ruler + dot */}
        {hoverSample && hoverX != null && (
          <>
            <line
              x1={hoverX}
              x2={hoverX}
              y1={CHART_PAD.top}
              y2={heightPx - CHART_PAD.bottom}
              stroke="#ffffff"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
            {hoverSample.elevFt != null && (
              <circle
                cx={hoverX}
                cy={yOf(hoverSample.elevFt)}
                r={3.5}
                fill="#f59e0b"
                stroke="#fff"
                strokeWidth={1.5}
              />
            )}
          </>
        )}
      </svg>

      {/* Hover callout */}
      {hoverSample && (
        <div className="px-3 pb-2 text-[11px] font-mono text-cockpit-200 leading-snug">
          {fmtDist(hoverSample.distNm)} NM &middot;{' '}
          {hoverSample.elevFt != null ? `${fmtAlt(hoverSample.elevFt)} ft MSL` : '— ft'}
          {hoverSample.aglFt != null && cruiseAltFt != null && (
            <span className={
              hoverSample.aglFt >= agleMinFt
                ? ' text-emerald-300'
                : hoverSample.aglFt >= AGL_AMBER_FT
                  ? ' text-amber-300'
                  : ' text-red-400'
            }>
              {' '}&middot; AGL{' '}
              {hoverSample.aglFt < 0 ? `${fmtAlt(-hoverSample.aglFt)} BELOW` : fmtAlt(hoverSample.aglFt) + ' ft'}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
